import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '../../App';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { toast } from 'sonner';
import { Camera, Award, Plus, Edit2, Trash2 } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../ui/alert-dialog';

export default function Profile({ user: initialUser }) {
  const navigate = useNavigate();
  const [user, setUser] = useState(initialUser);
  const [isEditing, setIsEditing] = useState(false);
  const [avatarUrl, setAvatarUrl] = useState(
    initialUser.avatar_url?.startsWith('http') 
      ? initialUser.avatar_url 
      : `${window.location.origin}/api/media-files${initialUser.avatar_url?.replace('/media', '') || ''}`
  );
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [awardToDelete, setAwardToDelete] = useState(null);
  const [loading, setLoading] = useState(false);

  // Calculate days since joining (from start of join date to start of today)
  const calculateDaysSinceJoining = (joinDate) => {
    if (!joinDate) return 0;
    
    // Parse the join date and set to start of day (00:00:00)
    const joinDateTime = new Date(joinDate);
    const joinDayStart = new Date(joinDateTime.getFullYear(), joinDateTime.getMonth(), joinDateTime.getDate());
    
    // Get start of today (00:00:00)
    const today = new Date();
    const todayStart = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    
    // Calculate difference in days
    const diffTime = todayStart - joinDayStart;
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
    
    // Return at least 1 day (same day registration counts as day 1)
    return Math.max(diffDays + 1, 1);
  };

  // Fetch latest user data on component mount
  useEffect(() => {
    fetchUserData();
  }, []);

  const fetchUserData = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/auth/me`);
      setUser(res.data);
      
      // Update avatar URL if changed
      const newAvatarUrl = res.data.avatar_url?.startsWith('http') 
        ? res.data.avatar_url 
        : `${window.location.origin}/api/media-files${res.data.avatar_url?.replace('/media', '') || ''}`;
      setAvatarUrl(newAvatarUrl);
    } catch (error) {
      console.error('獲取用戶數據錯誤:', error);
      toast.error('載入用戶資料失敗');
    } finally {
      setLoading(false);
    }
  };

  const handleAvatarUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('file', file);

    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(`${API}/media/upload`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          'Authorization': `Bearer ${token}`
        }
      });
      const mediaUrl = res.data.media_url;
      setAvatarUrl(`${window.location.origin}/api/media-files${mediaUrl.replace('/media', '')}`);
      await axios.put(`${API}/users/${user.user_id}`, { avatar_url: mediaUrl });
      toast.success('頭像已更新');
    } catch (error) {
      toast.error('上傳失敗');
    }
  };

  const handleEditAward = (award, index) => {
    navigate('/student/add-award', { state: { award, index } });
  };

  const handleDeleteAward = async (index) => {
    try {
      // Use current state data
      const currentAwards = user.awards || [];
      const updatedAwards = currentAwards.filter((_, i) => i !== index);
      
      await axios.put(`${API}/users/${user.user_id}`, {
        awards: updatedAwards
      });
      
      toast.success('獲獎紀錄已刪除');
      setDeleteDialogOpen(false);
      setAwardToDelete(null);
      
      // Refresh user data
      fetchUserData();
    } catch (error) {
      console.error('刪除獲獎紀錄錯誤:', error);
      toast.error('刪除失敗');
    }
  };

  // Sort awards by date (newest first)
  const sortedAwards = user.awards ? [...user.awards].sort((a, b) => {
    return new Date(b.date) - new Date(a.date);
  }) : [];

  return (
    <div className="p-4 md:p-8" data-testid="profile-page">
      <div className="max-w-4xl mx-auto">
        <h1 className="text-3xl md:text-4xl font-bold text-gray-800 mb-6 md:mb-8">我的帳戶</h1>

        {loading ? (
          <div className="text-center py-8">載入中...</div>
        ) : (
          <div className="bg-white rounded-2xl shadow-lg p-4 md:p-8">
          {/* Avatar Section */}
          <div className="flex flex-col items-center mb-6 md:mb-8">
            <div className="relative">
              <img 
                src={avatarUrl} 
                alt={user.username}
                className="w-24 h-24 md:w-32 md:h-32 rounded-full object-cover border-4 border-orange-500"
                data-testid="user-avatar"
              />
              <label className="absolute bottom-0 right-0 bg-orange-500 p-2 rounded-full cursor-pointer hover:bg-orange-600">
                <Camera className="text-white" size={16} />
                <input 
                  type="file" 
                  accept="image/*"
                  onChange={handleAvatarUpload}
                  className="hidden"
                  data-testid="avatar-upload-input"
                />
              </label>
            </div>
            <h2 className="text-xl md:text-2xl font-bold text-gray-800 mt-4">{user.username}</h2>
            {user.student_id && (
              <p className="text-gray-600 text-sm md:text-base" data-testid="profile-student-id">學生ID: {user.student_id}</p>
            )}
          </div>

          {/* Stats Grid */}
          <div className="grid grid-cols-3 gap-2 md:gap-6 mb-6 md:mb-8">
            <div className="bg-gradient-to-br from-orange-50 to-orange-100 rounded-xl p-3 md:p-6 text-center">
              <div className="text-2xl md:text-4xl font-bold text-orange-600" data-testid="profile-points">{user.points}</div>
              <div className="text-gray-700 mt-1 md:mt-2 text-xs md:text-base">累積積分</div>
            </div>
            <div className="bg-gradient-to-br from-teal-50 to-teal-100 rounded-xl p-3 md:p-6 text-center">
              <div className="text-2xl md:text-4xl font-bold text-teal-600">
                {calculateDaysSinceJoining(user.join_date)}
              </div>
              <div className="text-gray-700 mt-1 md:mt-2 text-xs md:text-base">加入天數</div>
            </div>
            <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl p-3 md:p-6 text-center">
              <div className="text-2xl md:text-4xl font-bold text-blue-600">{user.awards?.length || 0}</div>
              <div className="text-gray-700 mt-1 md:mt-2 text-xs md:text-base">獲獎次數</div>
            </div>
          </div>

          {/* Profile Info */}
          <div className="space-y-4 mb-6 md:mb-8">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-gray-600">用戶名稱</label>
                <Input value={user.username} disabled className="mt-1" />
              </div>
              <div>
                <label className="text-sm font-medium text-gray-600">電郵</label>
                <Input value={user.email} disabled className="mt-1" />
              </div>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-600">電話</label>
              <Input value={user.phone} disabled className="mt-1" />
            </div>
          </div>

          {/* Awards Section */}
          <div>
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <Award className="text-yellow-500" size={20} />
                <h3 className="text-lg md:text-xl font-bold text-gray-800">個人獲獎成績</h3>
              </div>
              <Button
                onClick={() => navigate('/student/add-award')}
                className="bg-yellow-500 hover:bg-yellow-600"
                size="sm"
              >
                <Plus size={16} className="mr-1" />
                添加
              </Button>
            </div>
            
            {!user.awards || user.awards.length === 0 ? (
              <div className="text-center text-gray-500 py-8 text-sm md:text-base">暫無獲獎紀錄</div>
            ) : (
              <div className="space-y-3">
                {sortedAwards.map((award, index) => {
                  // Find original index for proper deletion/editing
                  const originalIndex = user.awards.findIndex(a => 
                    a.event_name === award.event_name && 
                    a.competition_item === award.competition_item && 
                    a.date === award.date
                  );
                  
                  return (
                    <div key={index} className="bg-yellow-50 rounded-lg p-3 md:p-4 border border-yellow-200" data-testid="award-item">
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <div className="font-semibold text-gray-800 text-sm md:text-base">{award.event_name}</div>
                          <div className="text-sm text-gray-600 mt-1">{award.competition_item}</div>
                          <div className="flex items-center gap-2 mt-2">
                            <span className={`px-2 py-1 rounded text-xs md:text-sm font-medium ${
                              award.ranking === '冠軍' ? 'bg-yellow-200 text-yellow-800' :
                              award.ranking === '亞軍' ? 'bg-gray-200 text-gray-800' :
                              award.ranking === '季軍' ? 'bg-orange-200 text-orange-800' :
                              'bg-blue-200 text-blue-800'
                            }`}>
                              {award.ranking}
                            </span>
                            {award.date && (
                              <span className="text-xs text-gray-500">{award.date}</span>
                            )}
                          </div>
                        </div>
                        <div className="flex gap-2 ml-2">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleEditAward(award, originalIndex)}
                            className="h-8 w-8 p-0 text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                          >
                            <Edit2 size={16} />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => {
                              setAwardToDelete(originalIndex);
                              setDeleteDialogOpen(true);
                            }}
                            className="h-8 w-8 p-0 text-red-600 hover:text-red-700 hover:bg-red-50"
                          >
                            <Trash2 size={16} />
                          </Button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
        )}
      </div>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>確認刪除</AlertDialogTitle>
            <AlertDialogDescription>
              您確定要刪除這項獲獎紀錄嗎？此操作無法撤銷。
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>取消</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => handleDeleteAward(awardToDelete)}
              className="bg-red-600 hover:bg-red-700"
            >
              刪除
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
